import { useState } from 'react'
import Header from './components/Header'
import Footer from './components/Footer'
import FileUpload from './components/FileUpload'
import { compressPDF, formatFileSize, type CompressionOptions, type CompressionResult } from './utils/pdfCompressor'
import './styles/App.css'

function App() {
  const [file, setFile] = useState<File | null>(null)
  const [processing, setProcessing] = useState(false)
  const [result, setResult] = useState<CompressionResult | null>(null)
  const [quality, setQuality] = useState<'low' | 'medium' | 'high'>('medium')
  const [removeMetadata, setRemoveMetadata] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile)
    setResult(null)
    setError(null)
  }

  const handleCompress = async () => {
    if (!file) return

    setProcessing(true)
    setError(null)

    try {
      const options: CompressionOptions = {
        quality,
        removeMetadata,
      }

      const compressionResult = await compressPDF(file, options)
      setResult(compressionResult)
    } catch (err) {
      console.error('Compression error:', err)
      setError(err instanceof Error ? err.message : 'Failed to compress PDF')
    } finally {
      setProcessing(false)
    }
  }

  const handleDownload = () => {
    if (!result) return

    const url = URL.createObjectURL(result.compressedBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = file?.name.replace('.pdf', '-compressed.pdf') || 'compressed.pdf'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleReset = () => {
    setFile(null)
    setResult(null)
    setError(null)
  }

  return (
    <div className="app">
      <Header
        title="PDF Compressor"
        description="Compress PDF files locally - 100% privacy, no upload required"
      />

      <main className="main-content">
        <div className="container">
          {!file && (
            <FileUpload
              onFileSelect={handleFileSelect}
              accept=".pdf,application/pdf"
              maxSize={50 * 1024 * 1024} // 50MB
            />
          )}

          {file && !result && (
            <div className="file-info">
              <h3>Selected File</h3>
              <p><strong>Name:</strong> {file.name}</p>
              <p><strong>Size:</strong> {formatFileSize(file.size)}</p>

              <div className="compression-options">
                <h3>Compression Options</h3>

                <div className="option-group">
                  <label>Quality Level:</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="low"
                        checked={quality === 'low'}
                        onChange={() => setQuality('low')}
                      />
                      <span>Low Quality (Smallest file)</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="medium"
                        checked={quality === 'medium'}
                        onChange={() => setQuality('medium')}
                      />
                      <span>Medium Quality (Balanced)</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        value="high"
                        checked={quality === 'high'}
                        onChange={() => setQuality('high')}
                      />
                      <span>High Quality (Best quality)</span>
                    </label>
                  </div>
                </div>

                <div className="option-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={removeMetadata}
                      onChange={(e) => setRemoveMetadata(e.target.checked)}
                    />
                    <span>Remove metadata (author, title, etc.)</span>
                  </label>
                </div>
              </div>

              <div className="button-group">
                <button
                  onClick={handleCompress}
                  disabled={processing}
                  className="btn-primary"
                >
                  {processing ? 'Compressing...' : 'Compress PDF'}
                </button>
                <button onClick={handleReset} className="btn-secondary">
                  Choose Different File
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {result && (
            <div className="result-info">
              <h3>✅ Compression Complete!</h3>

              <div className="result-stats">
                <div className="stat">
                  <span className="stat-label">Original Size:</span>
                  <span className="stat-value">{formatFileSize(result.originalSize)}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Compressed Size:</span>
                  <span className="stat-value">{formatFileSize(result.compressedSize)}</span>
                </div>
                <div className="stat highlight">
                  <span className="stat-label">Savings:</span>
                  <span className="stat-value">{result.compressionRatio.toFixed(1)}%</span>
                </div>
              </div>

              <div className="button-group">
                <button onClick={handleDownload} className="btn-primary">
                  Download Compressed PDF
                </button>
                <button onClick={handleReset} className="btn-secondary">
                  Compress Another File
                </button>
              </div>
            </div>
          )}

          <div className="privacy-notice">
            <h3>🔒 100% Privacy Guaranteed</h3>
            <p>
              All PDF compression happens entirely in your browser using WebAssembly.
              Your files never leave your device - no server uploads, no storage, no tracking.
            </p>
          </div>

          <div className="features-list">
            <h3>Features</h3>
            <ul>
              <li>✅ Multiple compression levels (low, medium, high quality)</li>
              <li>✅ Optional metadata removal for extra privacy</li>
              <li>✅ Support for PDFs up to 50MB</li>
              <li>✅ Fast processing with pdf-lib</li>
              <li>✅ No file size limits or watermarks</li>
              <li>✅ Works completely offline (once loaded)</li>
            </ul>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default App
